// Story 8.10 (ADO-2019), AC3 "they are told it has happened": the result of accepting a
// handheld order (the 8.10 Figma exports, "HHT · Added to carts", "Q3 · Added, some lines not
// added" and "Q6 · Order closed, nothing added"): what was added and one row per depot cart
// that received a line, with "Go to cart". When lines were not added the summary says so and
// points at the "Not added" list below (`HhtNotAddedList`, Q3). An accept with nothing added
// is the "Order closed" panel, saying nothing was added, with no cart row (Q6, DV-23).
//
// "Go to cart" opens the cart the accept wrote to (DV-68): the depot's primary entry, or for the
// Chill Fruit & Veg cart (Enabler E8-G, ADO-3109, task 5.1) the Fruit & Veg entry at that depot,
// on the account the server keyed the cart on (`customerAccountCode`). For an environment cart
// that is the tile's sole account; for the Default VC cart (phase 4) it is the Default VC
// account, which a Value Centre tile offering several accounts would not pick. It then opens /cart,
// which reads the active cart: each depot is a separate order. With other orders still
// waiting, a primary "Review next order" leads to the next one (DV-34); otherwise only "Back
// to Select Depot" shows.
import type { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import { Icon } from '../../design-system';
import { useSessionStore } from '../../auth/useSessionStore';
import type { HhtAcceptanceResult, HhtCartAdded } from '../../api/hht-orders.client';
import { DepotMark } from './DepotMark';
import { linesAndCases, linesLabel } from './hhtFormat';
import { CHOOSE_ROUTE, primaryEntryAtDepot } from '../../ordering/orderingChoices';
import { selectOrderingChoice } from '../../ordering/orderingSession';
import linkButton from '../Cart/LinkButton.module.css';
import styles from './HhtOrders.module.css';

export function HhtAddedToCarts({
  result,
  notAddedCount,
  nextOrderPath,
}: {
  readonly result: HhtAcceptanceResult;
  /** How many lines the "Not added" list below shows (Q3). */
  readonly notAddedCount: number;
  /** The next waiting order's review, or null when nothing else waits. */
  readonly nextOrderPath: string | null;
}) {
  const navigate = useNavigate();
  const setActiveDepot = useSessionStore((s) => s.setActiveDepot);

  const goToCart = (cart: HhtCartAdded) => {
    const choices = useSessionStore.getState().orderingChoices;
    const entry =
      choices === null
        ? null
        : cart.entryKind === undefined
          ? primaryEntryAtDepot(choices.entries, cart.depotId)
          : (choices.entries.find(
              (each) => each.depotId === cart.depotId && each.kind === cart.entryKind,
            ) ?? null);
    if (entry === null || !selectOrderingChoice(entry.key, cart.customerAccountCode)) {
      setActiveDepot(cart.depotId);
    }
    navigate('/cart');
  };

  const closed = result.added.lineCount === 0;
  const carts = result.carts.filter((cart) => cart.lineCount > 0);
  let summary: ReactNode;
  if (closed) {
    summary = (
      <>
        <strong>{linesLabel(0)}</strong> added. None of this order&apos;s lines could be added, so
        it&apos;s closed and recorded in your HHT history.
      </>
    );
  } else if (notAddedCount > 0) {
    summary = (
      <>
        <strong>{linesAndCases(result.added.lineCount, result.added.caseCount)}</strong> added.{' '}
        {notAddedCount === 1 ? '1 line' : `${notAddedCount} lines`} couldn&apos;t be added — see
        below. Open a cart to review and send.
      </>
    );
  } else {
    summary = (
      <>
        <strong>{linesAndCases(result.added.lineCount, result.added.caseCount)}</strong> added. Open
        a cart to review and send — each depot is a separate order.
      </>
    );
  }

  return (
    <section className={`${styles.panel} ${styles.added}`} aria-labelledby="hht-added-title">
      <h2 id="hht-added-title" className={styles.addedTitle}>
        {closed ? 'Order closed — nothing was added' : 'Added to carts — not sent'}
      </h2>
      <p className={styles.addedSummary}>{summary}</p>
      {carts.length > 0 ? (
        <ul className={styles.list}>
          {carts.map((cart) => (
            <li key={`${cart.depotId}:${cart.entryKind ?? 'DEPOT'}`} className={styles.cartRow}>
              <DepotMark depotCode={cart.depotCode} />
              <span className={styles.cartText}>
                <strong>{cart.depotName} cart</strong>
                <span>{linesAndCases(cart.lineCount, cart.caseCount)}</span>
              </span>
              <button
                type="button"
                className={styles.goToCart}
                aria-label={`Go to the ${cart.depotName} cart`}
                onClick={() => goToCart(cart)}
              >
                Go to cart
                <Icon name="arrowRight" size={14} />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <div className={styles.addedActions}>
        {nextOrderPath !== null ? (
          <Link to={nextOrderPath} className={`${linkButton.linkButton} ${linkButton.primary}`}>
            Review next order
          </Link>
        ) : null}
        <Link to={CHOOSE_ROUTE} className={`${linkButton.linkButton} ${linkButton.secondary}`}>
          Back to Select Depot
        </Link>
      </div>
    </section>
  );
}
